import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSameActionMutation } from '@/hooks/useSingleFlightMutation';
import { api } from '@/lib/api';

/**
 * Story 49.3 — modelos de documento (tela de administração). Molde: hooks/useDocuments.ts.
 *
 * Fonte: rotas da 49.2/49.3 (`/documents/templates`). A allowlist de variáveis vem do
 * servidor (`GET /documents/templates/variables`) — UMA verdade só, nunca cópia local.
 */

export type DocumentTemplateKind = 'consent' | 'contract' | 'generic';

export interface DocumentTemplate {
  id: string;
  name: string;
  version: number;
  body: string;
  kind: DocumentTemplateKind;
  autoIssueOn: 'none' | 'first_session' | 'package_sale';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** variáveis usadas no corpo — calculadas pelo servidor na listagem */
  variables: string[];
}

const TEMPLATES_KEY = 'document-templates';

export const documentTemplatesKey = () => [TEMPLATES_KEY] as const;
export const documentTemplateVariablesKey = () => [TEMPLATES_KEY, 'variables'] as const;

// Story 70.22 — a mesma leitura na aba e na Configuração rápida (ADR 0002, D4).
export async function getDocumentTemplates(): Promise<DocumentTemplate[]> {
  const { data } = await api.get<DocumentTemplate[]>('/documents/templates?includeInactive=true');
  return data;
}

export function useDocumentTemplates(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: documentTemplatesKey(),
    queryFn: getDocumentTemplates,
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useDocumentTemplateVariables(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: documentTemplateVariablesKey(),
    queryFn: async () => {
      const { data } = await api.get<{ variables: string[] }>('/documents/templates/variables');
      return data.variables;
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

/** RC-015: toda mutação invalida a lista — o estado exibido nunca depende de refresh manual. */
export function useUpdateTemplateBody() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: async ({ templateId, body }: { templateId: string; body: string }) => {
      const { data } = await api.patch(`/documents/templates/${templateId}/body`, { body });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: documentTemplatesKey() }),
  });
}

// Story 70.5 — a gravação e o efeito de cache são os mesmos em Modelos de documento e na
// Configuração rápida (ADR 0002, D1.4).
export async function setDocumentTemplateActive(templateId: string, isActive: boolean) {
  const { data } = await api.patch(`/documents/templates/${templateId}/active`, { isActive });
  return data;
}

export function afterDocumentTemplateWrite(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: documentTemplatesKey() });
}

export function useSetTemplateActive() {
  const queryClient = useQueryClient();
  return useSameActionMutation({
    mutationFn: ({ templateId, isActive }: { templateId: string; isActive: boolean }) => setDocumentTemplateActive(templateId, isActive),
    onSuccess: () => afterDocumentTemplateWrite(queryClient),
  });
}
